//
// # AddSymbolPinDialog
//
// Modal dialog for configuring a new symbol pin before placement.
// Fields: Name, Width, Direction (input/output/inout), Shape (rectangle/polygon/ellipse).
//

import React, { useState, useCallback } from "react";
import { Modal } from "../Modal/Modal";
import { schema } from "GenAlphaSchema";

// Derive direction options from the schema PortDir constant.
const PORT_DIR_OPTIONS = Object.entries(schema.core.PortDir).map(
  ([label, value]) => ({ value, label })
);

// Pin shape options for the dialog (subset of schema PinShapeKind).
const PIN_SHAPE_OPTIONS = [
  { value: "rectangle", label: "Rectangle" },
  { value: "ellipse", label: "Ellipse" },
];

// Label position is a UI-only concept (not stored in schema).
// The schema stores the actual TextFormat (alignment/baseline).
export const LABEL_POSITIONS = {
  Left: "left",
  Right: "right",
  Above: "above",
  Below: "below",
} as const;

const LABEL_POSITION_OPTIONS = Object.entries(LABEL_POSITIONS).map(
  ([label, value]) => ({ value, label })
);

// The result returned when the user confirms the dialog.
export interface SymbolPinConfig {
  readonly portName: string;
  readonly width: number;
  readonly dir: schema.core.PortDir;
  readonly shape: schema.symbol.PinShapeKind;
  readonly labelPosition: string;
}

interface AddSymbolPinDialogProps {
  readonly onConfirm: (config: SymbolPinConfig) => void;
  readonly onCancel: () => void;
}

export function AddSymbolPinDialog({
  onConfirm,
  onCancel,
}: AddSymbolPinDialogProps) {
  const [portName, setPortName] = useState("pin0");
  const [dir, setDir] = useState<schema.core.PortDir>("input");
  const [shape, setShape] = useState<schema.symbol.PinShapeKind>("rectangle");
  const [labelPosition, setLabelPosition] = useState("left");

  const handleConfirm = useCallback(() => {
    // Bus width is fixed to 1 — our system only supports single-bit pins for now.
    onConfirm({ portName, width: 1, dir, shape, labelPosition });
  }, [portName, dir, shape, labelPosition, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  return (
    <Modal open={true} onClose={onCancel} title="Add Symbol Pin">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Port Name */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Name
          </label>
          <input
            type="text"
            value={portName}
            onChange={(e) => setPortName(e.target.value)}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={inputStyle}
          />
        </div>

        {/* Bus Width (fixed to 1 for now) */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Bus Width
          </label>
          <input
            type="number"
            value={1}
            disabled
            className="px-2 py-1.5 text-[13px] rounded border outline-none opacity-50 cursor-not-allowed"
            style={inputStyle}
          />
        </div>

        {/* Direction */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Direction
          </label>
          <select
            value={dir}
            onChange={(e) => setDir(e.target.value as schema.core.PortDir)}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {PORT_DIR_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        {/* Shape */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Shape
          </label>
          <select
            value={shape}
            onChange={(e) =>
              setShape(e.target.value as schema.symbol.PinShapeKind)
            }
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {PIN_SHAPE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Label Position */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Label Position
          </label>
          <select
            value={labelPosition}
            onChange={(e) => setLabelPosition(e.target.value)}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {LABEL_POSITION_OPTIONS.map((lp) => (
              <option key={lp.value} value={lp.value}>
                {lp.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor: "var(--color-ide-accent)",
              color: "#ffffff",
            }}
          >
            Add Pin
          </button>
        </div>
      </div>
    </Modal>
  );
}
